import { CSV_PROFILES } from './csv'
import {
  DEFAULT_SELECTED_COLUMN_IDS,
  EXPORT_COLUMN_LIMIT
} from './csv-export-columns'
import { upsertMetaEntry } from './table-column-settings'

/**
 * Reine Helfer fuer die gemerkte Exportauswahl. Kein React, keine
 * Netzwerkzugriffe, damit die Logik ohne Rendering testbar bleibt.
 *
 * Gespeichert wird ein eigener Eintrag im Array Space.customer_dashboard_meta:
 * { id: 'UI_CSV_EXPORT', version: 1, tables: { talents: { selected, profile, updated_at } } }
 *
 * Drei bewusste Abweichungen von UI_TABLE_SETTINGS:
 *
 * 1. Gespeichert wird die Positivauswahl, nicht die Abwahl. Dort ist der
 *    Ausgangszustand "alles sichtbar", hier "fast nichts ausgewaehlt".
 * 2. Unbekannte Ids werden nicht unbegrenzt mitgefuehrt. Feld-UUIDs sterben mit
 *    geloeschten Stellen, ueber Jahre kaemen tausende tote Ids zusammen. Die
 *    Liste ist nach Aktualitaet sortiert und bei EXPORT_COLUMN_LIMIT gekappt,
 *    aktuell aufloesbare Ids stehen immer vorn.
 * 3. Die Groesse zaehlt doppelt, weil customer_dashboard_meta im SPACE-Fragment
 *    steckt und die Nutzerverwaltung damit alle Employee-Spaces laedt.
 */

export const CSV_EXPORT_META_ID = 'UI_CSV_EXPORT'
const CSV_EXPORT_VERSION = 1

const isPlainObject = (value) =>
  !!value && typeof value === 'object' && !Array.isArray(value)

const toIdList = (value, limit = EXPORT_COLUMN_LIMIT) => {
  if (!Array.isArray(value)) return []

  const seen = new Set()
  const result = []

  for (const item of value) {
    if (typeof item !== 'string' || !item || seen.has(item)) continue
    seen.add(item)
    result.push(item)
    if (result.length >= limit) break
  }

  return result
}

const toProfile = (value) =>
  Object.values(CSV_PROFILES).includes(value) ? value : null

/**
 * Liest den Eintrag aus dem normalisierten Meta-Array. Liefert null, wenn
 * nichts gespeichert ist oder die Form nicht passt. Wirft nie.
 */
export const readCsvExportSettings = (metaArray, tableId) => {
  const entry = (Array.isArray(metaArray) ? metaArray : []).find(
    (item) => isPlainObject(item) && item.id === CSV_EXPORT_META_ID
  )

  if (!entry) return null
  if ((entry.version ?? CSV_EXPORT_VERSION) !== CSV_EXPORT_VERSION) return null
  if (!isPlainObject(entry.tables)) return null

  const table = entry.tables[tableId]
  if (!isPlainObject(table)) return null

  return {
    selected: toIdList(table.selected),
    profile: toProfile(table.profile)
  }
}

/**
 * Welche Spalten sind beim Oeffnen angehakt. Ohne gespeicherten Stand greift
 * der bisherige Werkszustand.
 *
 * @param {Object} input
 * @param {Object|null} input.stored
 * @param {Array}  input.columns  die aktuell verfuegbaren Spalten
 * @returns {Set<string>}
 */
export const resolveSelectedIds = ({ stored, columns = [] }) => {
  const available = new Set(columns.map(({ id }) => id))
  const wanted = stored ? stored.selected : DEFAULT_SELECTED_COLUMN_IDS

  const selected = new Set(wanted.filter((id) => available.has(id)))

  columns.forEach((column) => {
    if (column.required) selected.add(column.id)
  })

  return selected
}

/**
 * Baut die zu speichernde Liste. Die aktuelle Auswahl steht vorn, danach die
 * frueher gemerkten Ids, die sich in dieser Auswahl nicht aufloesen liessen.
 * Aufloesbare, aber abgewaehlte Ids fallen raus, der Nutzer hat sie gerade
 * bewusst abgewaehlt.
 *
 * @param {Object} input
 * @param {Array<string>} input.selectedIds   in Spaltenreihenfolge
 * @param {Array<string>} input.previousIds   zuletzt gespeicherte Liste
 * @param {Set<string>|Array<string>} input.resolvableIds
 * @returns {Array<string>}
 */
export const capSelection = ({
  selectedIds = [],
  previousIds = [],
  resolvableIds = []
}) => {
  const resolvable = new Set(resolvableIds)
  const selected = new Set(selectedIds)

  const carried = previousIds.filter(
    (id) => !selected.has(id) && !resolvable.has(id)
  )

  return toIdList([...selectedIds, ...carried])
}

/**
 * Baut den neuen UI_CSV_EXPORT-Eintrag. Ein Eintrag einer neueren Version wird
 * nicht angefasst.
 */
export const buildCsvExportEntry = (
  metaArray,
  tableId,
  { selectedIds, resolvableIds, profile },
  now = new Date().toISOString()
) => {
  const existing = (Array.isArray(metaArray) ? metaArray : []).find(
    (item) => isPlainObject(item) && item.id === CSV_EXPORT_META_ID
  )

  if (
    existing &&
    (existing.version ?? CSV_EXPORT_VERSION) !== CSV_EXPORT_VERSION
  ) {
    return existing
  }

  const tables = isPlainObject(existing?.tables) ? { ...existing.tables } : {}
  const previousIds = toIdList(tables[tableId]?.selected)

  tables[tableId] = {
    selected: capSelection({ selectedIds, previousIds, resolvableIds }),
    profile: toProfile(profile) || CSV_PROFILES.STANDARD,
    updated_at: now
  }

  return { id: CSV_EXPORT_META_ID, version: CSV_EXPORT_VERSION, tables }
}

/**
 * Meta-Array plus neuer Eintrag als JSON-String, bereit fuer updateSpace.
 * Alle anderen Eintraege, insbesondere UI_TABLE_SETTINGS, bleiben unberuehrt.
 */
export const withCsvExportMeta = (metaArray, tableId, settings, now) =>
  JSON.stringify(
    upsertMetaEntry(
      Array.isArray(metaArray) ? metaArray : [],
      CSV_EXPORT_META_ID,
      buildCsvExportEntry(metaArray, tableId, settings, now)
    )
  )
